import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import { WIZARD } from "@/lib/content";
import { api } from "@/lib/api";
import { EASE } from "@/components/motion/Reveal";
import { Button } from "@/components/motion/Button";

const STEPS = [
  { id: 1, label: "Objekt", q: "Was möchten Sie reinigen lassen?" },
  { id: 2, label: "Reinigung", q: "Welche Reinigung?" },
  { id: 3, label: "Details", q: "Objektinformationen" },
  { id: 4, label: "Zusatz", q: "Zusatzleistungen" },
  { id: 5, label: "Kontakt", q: "Wie erreichen wir Sie?" },
];

const initial = (preset) => ({
  object_type: preset?.object_type || "",
  cleaning_types: preset?.cleaning_types || [],
  location: "", size: "", rooms: "", square_meters: "", preferred_date: "",
  extras: [], name: "", company: "", phone: "", email: "", message: "", photos: [],
});

const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

export function Tile({ selected, onClick, children, testid, multi = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={selected}
      className={`group relative min-h-[72px] md:min-h-[84px] px-5 py-4 text-left border transition-colors duration-500 ease-out flex items-center justify-between gap-3 ${
        selected ? "bg-ink text-paper border-ink" : "border-line text-ink hover:border-ink bg-transparent"
      }`}
    >
      <span className="text-sm md:text-base">{children}</span>
      <span className={`shrink-0 w-4 h-4 border flex items-center justify-center transition-colors duration-500 ${multi ? "" : "rounded-full"} ${selected ? "border-paper bg-paper text-ink" : "border-line group-hover:border-ink"}`}>
        {selected && <Check size={10} strokeWidth={2.5} />}
      </span>
    </button>
  );
}

const Field = ({ label, testid, required, ...props }) => (
  <label className="block">
    <span className="label text-mute">{label}{required && <span className="text-signal"> *</span>}</span>
    <input data-testid={testid} className="field mt-1" {...props} />
  </label>
);

export function QuoteWizard({ preset, compact = false }) {
  const [step, setStep] = useState(1);
  const [dir, setDir] = useState(1);
  const [data, setData] = useState(() => initial(preset));
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(null);
  const fileRef = useRef(null);
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggle = (k, v) => setData((d) => ({ ...d, [k]: d[k].includes(v) ? d[k].filter((x) => x !== v) : [...d[k], v] }));

  const validate = () => {
    const e = {};
    if (step === 1 && !data.object_type) e.object_type = "Bitte wählen Sie ein Objekt.";
    if (step === 2 && !data.cleaning_types.length) e.cleaning_types = "Bitte wählen Sie mindestens eine Reinigung.";
    if (step === 3 && !data.location.trim()) e.location = "Bitte geben Sie Ort oder PLZ an.";
    if (step === 5) {
      if (!data.name.trim()) e.name = "Bitte geben Sie Ihren Namen an.";
      if (!data.phone.trim()) e.phone = "Bitte geben Sie Ihre Telefonnummer an.";
      if (!emailOk(data.email)) e.email = "Bitte geben Sie eine gültige E-Mail-Adresse an.";
    }
    setErrors(e);
    return !Object.keys(e).length;
  };

  const next = () => {
    if (!validate()) return;
    setDir(1);
    setStep((s) => Math.min(5, s + 1));
  };
  const back = () => {
    setDir(-1);
    setErrors({});
    setStep((s) => Math.max(1, s - 1));
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 8 - data.photos.length);
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      try {
        const rec = await api.upload(f);
        setData((d) => ({ ...d, photos: [...d.photos, rec] }));
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Upload von ${f.name} fehlgeschlagen.`);
      }
    }
    setUploading(false);
    e.target.value = "";
  };

  const sendWeb3forms = async (quote) => {
    const key = process.env.REACT_APP_WEB3FORMS_KEY;
    if (!key) return;
    const detail = [
      `Referenz: ${quote.reference}`,
      `Objekt: ${quote.object_type}`,
      `Reinigung: ${quote.cleaning_types.join(", ") || "–"}`,
      `Ort / PLZ: ${quote.location}`,
      `Grösse: ${quote.size || "–"}`,
      `Zimmer: ${quote.rooms || "–"}`,
      `Quadratmeter: ${quote.square_meters || "–"}`,
      `Termin: ${quote.preferred_date || "–"}`,
      `Zusatzleistungen: ${quote.extras.join(", ") || "–"}`,
      `Firma: ${quote.company || "–"}`,
      `Fotos: ${quote.photo_ids.length} hochgeladen`,
    ].join("\n");
    try {
      await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          access_key: key,
          subject: `Offertanfrage ${quote.reference} – ${quote.object_type}`,
          from_name: "Emini Reinigung Website",
          name: quote.name,
          email: quote.email,
          phone: quote.phone,
          message: `${quote.message ? quote.message + "\n\n" : ""}${detail}`,
        }),
      });
    } catch (err) {
      // Benachrichtigung fehlgeschlagen – Anfrage ist dennoch gespeichert
    }
  };

  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const { photos, ...rest } = data;
      const res = await api.createQuote({ ...rest, photo_ids: photos.map((p) => p.file_id) });
      await sendWeb3forms(res);
      setDone(res);
    } catch (err) {
      toast.error("Die Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es erneut.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="quote-success" className="py-16 md:py-24">
        <p className="label text-signal mb-6">Anfrage erhalten</p>
        <h3 className="display text-4xl sm:text-5xl lg:text-6xl text-ink">Vielen Dank,<br />{done.name.split(" ")[0]}.</h3>
        <p className="mt-8 text-base md:text-lg text-mute max-w-md leading-relaxed">Wir haben Ihre Anfrage erhalten und melden uns in der Regel innerhalb von ein bis zwei Arbeitstagen.</p>
        <p className="mt-8 label text-mute">Referenz <span data-testid="quote-reference" className="text-ink ml-2">{done.reference}</span></p>
      </div>
    );
  }

  const current = STEPS[step - 1];

  return (
    <div data-testid="quote-wizard" className={compact ? "" : "py-4"}>
      <ol className="grid grid-cols-5 gap-2 md:gap-3" aria-label="Fortschritt">
        {STEPS.map((s) => (
          <li key={s.id} className="flex flex-col gap-3">
            <span className="h-px bg-line relative overflow-hidden">
              <motion.span className="absolute inset-0 bg-ink origin-left" initial={false} animate={{ scaleX: s.id <= step ? 1 : 0 }} transition={{ duration: 0.8, ease: EASE }} />
            </span>
            <span className={`label hidden sm:flex items-center gap-2 transition-colors duration-500 ${s.id === step ? "text-ink" : "text-mute/60"}`}>
              {s.id === step && <span className="w-1.5 h-1.5 rounded-full bg-signal" />}
              0{s.id} {s.label}
            </span>
          </li>
        ))}
      </ol>
      <p className="label text-mute mt-4 sm:hidden">Schritt 0{step} / 05 · {current.label}</p>

      <div className="mt-10 md:mt-14 min-h-[360px]">
        <AnimatePresence mode="wait" custom={dir}>
          <motion.div
            key={step}
            custom={dir}
            initial={{ opacity: 0, x: dir * 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: dir * -40 }}
            transition={{ duration: 0.6, ease: EASE }}
          >
            <h3 data-testid="quote-wizard-question" className="display text-3xl sm:text-4xl lg:text-5xl text-ink mb-10">{current.q}</h3>

            {step === 1 && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {WIZARD.objects.map((o, i) => (
                  <Tile key={o} selected={data.object_type === o} onClick={() => set("object_type", o)} testid={i === 0 ? "quote-wizard-step-1-option" : `quote-object-${i}`}>{o}</Tile>
                ))}
              </div>
            )}
            {step === 2 && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {WIZARD.cleanings.map((c, i) => (
                  <Tile key={c} multi selected={data.cleaning_types.includes(c)} onClick={() => toggle("cleaning_types", c)} testid={`quote-cleaning-${i}`}>{c}</Tile>
                ))}
              </div>
            )}
            {step === 3 && (
              <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
                <Field label="Ort / PLZ" required testid="quote-location" value={data.location} onChange={(e) => set("location", e.target.value)} placeholder="z. B. 8001 Zürich" autoComplete="postal-code" />
                <Field label="Gewünschter Termin" testid="quote-date" value={data.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} placeholder="Datum oder Zeitraum" />
                <div>
                  <span className="label text-mute">Objektgrösse</span>
                  <div className="grid grid-cols-2 gap-2 mt-4">
                    {WIZARD.sizes.map((s) => <Tile key={s} selected={data.size === s} onClick={() => set("size", s)}>{s}</Tile>)}
                  </div>
                </div>
                <div>
                  <span className="label text-mute">Zimmer</span>
                  <div className="grid grid-cols-3 gap-2 mt-4">
                    {WIZARD.rooms.map((r) => <Tile key={r} selected={data.rooms === r} onClick={() => set("rooms", r)}>{r}</Tile>)}
                  </div>
                  <Field label="Quadratmeter (optional)" testid="quote-sqm" value={data.square_meters} onChange={(e) => set("square_meters", e.target.value)} placeholder="z. B. 95" inputMode="numeric" />
                </div>
              </div>
            )}
            {step === 4 && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {WIZARD.extras.map((x, i) => (
                  <Tile key={x} multi selected={data.extras.includes(x)} onClick={() => toggle("extras", x)} testid={`quote-extra-${i}`}>{x}</Tile>
                ))}
              </div>
            )}
            {step === 5 && (
              <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
                <Field label="Name" required testid="quote-name" value={data.name} onChange={(e) => set("name", e.target.value)} autoComplete="name" />
                <Field label="Firma (optional)" testid="quote-company" value={data.company} onChange={(e) => set("company", e.target.value)} autoComplete="organization" />
                <Field label="Telefon" required testid="quote-phone" type="tel" value={data.phone} onChange={(e) => set("phone", e.target.value)} autoComplete="tel" />
                <Field label="E-Mail" required testid="quote-email" type="email" value={data.email} onChange={(e) => set("email", e.target.value)} autoComplete="email" />
                <label className="block md:col-span-2">
                  <span className="label text-mute">Bemerkungen (optional)</span>
                  <textarea data-testid="quote-message" rows={3} className="field mt-1 resize-none" value={data.message} onChange={(e) => set("message", e.target.value)} placeholder="Zugang, Besonderheiten, Wünsche" />
                </label>
                <div className="md:col-span-2">
                  <span className="label text-mute">Fotos (optional, max. 8)</span>
                  <div className="mt-4 flex flex-wrap gap-3">
                    {data.photos.map((p) => (
                      <div key={p.file_id} className="relative w-20 h-20 bg-stone overflow-hidden" data-testid="quote-photo-thumb">
                        <img src={api.fileUrl(p.file_id)} alt={p.original_filename} className="w-full h-full object-cover" />
                        <button type="button" aria-label="Foto entfernen" onClick={() => set("photos", data.photos.filter((x) => x.file_id !== p.file_id))} className="absolute top-1 right-1 w-6 h-6 bg-ink text-paper flex items-center justify-center">
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                    {data.photos.length < 8 && (
                      <button type="button" data-testid="quote-upload-button" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-20 h-20 border border-dashed border-line hover:border-ink transition-colors flex flex-col items-center justify-center gap-1 label text-[9px] text-mute disabled:opacity-50">
                        <ImagePlus size={16} strokeWidth={1.25} />
                        {uploading ? "…" : "Foto"}
                      </button>
                    )}
                    <input ref={fileRef} data-testid="quote-file-input" type="file" accept="image/*" multiple className="hidden" onChange={onFiles} />
                  </div>
                </div>
              </div>
            )}

            {Object.values(errors)[0] && (
              <p data-testid="quote-error" className="mt-6 text-sm text-[#a3452b]">{Object.values(errors)[0]}</p>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="mt-12 flex items-center justify-between gap-4 border-t border-line pt-8">
        <button type="button" onClick={back} disabled={step === 1} data-testid="quote-wizard-back-button" className="label inline-flex items-center gap-2 text-ink disabled:opacity-30 disabled:pointer-events-none link-line py-2">
          <ArrowLeft size={12} strokeWidth={1.5} /> Zurück
        </button>
        {step < 5 ? (
          <Button onClick={next} data-testid="quote-wizard-next-button" arrow={false} magnetic={false}>
            Weiter <ArrowRight size={12} strokeWidth={1.5} />
          </Button>
        ) : (
          <Button onClick={submit} disabled={submitting} data-testid="quote-wizard-submit-button" magnetic={false}>
            {submitting ? "Wird gesendet …" : "Kostenlose Offerte anfragen"}
          </Button>
        )}
      </div>
    </div>
  );
}
